import { formatDate, t } from "@hakili/i18n";
import { GuardedButton, Modal } from "@hakili/ui";
import { Search } from "lucide-react";
import { useState } from "react";

import { fill } from "./partner-cards.js";

/**
 * Round 8, `M2` (WF-CON-40 extended to client accounts, 16 October 2026): **« Comptes clients », the
 * administration's way to find a client and cut or restore the access, with the reason written down.**
 *
 * The minimum the ruling authorises and nothing beside it: a search (name or telephone number, two characters at
 * least, because a screen that lists every borrower is a leak waiting for a screenshot), a status chip, and one
 * gesture per row behind a dialog that asks for the reason. Deactivation is not erasure and the dialog says so in
 * words; nothing else on the console changes for it.
 *
 * The screen holds no rule. The right (the administration alone, the Comptes screen's own), the reason's
 * necessity and the account's state all belong to the door (`/api/v1/clients`); the button is disabled while the
 * reason is blank because a request the door would refuse is not worth sending, and the door refuses it anyway.
 */

export interface ClientAccountView {
  readonly clientId: string;
  readonly fullName: string;
  readonly phone: string;
  readonly status: "actif" | "desactive";
  readonly deactivatedAt: string | null;
  readonly deactivationReason: string | null;
  readonly anonymised: boolean;
}

export interface ClientAccountDoors {
  readonly findClients: (query: string) => Promise<readonly ClientAccountView[]>;
  readonly deactivateClient: (clientId: string, reason: string) => Promise<void>;
  readonly reactivateClient: (clientId: string, reason: string) => Promise<void>;
  /** Round 9, `M3`: the irreversible one. The name typed back travels with the reason, and the door checks it. */
  readonly eraseClient: (
    clientId: string,
    input: { reason: string; confirmFullName: string },
  ) => Promise<void>;
}

const MIN_QUERY = 2;
type Gesture = "deactivate" | "reactivate";

const messageOf = (failure: unknown): string =>
  (failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee");

const dateWord = (iso: string): string => formatDate(new Date(iso));

export interface ClientAccountsProps extends ClientAccountDoors {
  /** `effacerComptesClients`, 06 §6.1.5 row 33. Without it the gesture is shown greyed, with the reason, and calls nothing. */
  readonly mayErase: boolean;
}

export function ClientAccounts(props: ClientAccountsProps): React.JSX.Element {
  const [query, setQuery] = useState("");
  const [found, setFound] = useState<readonly ClientAccountView[] | null>(null);
  const [searched, setSearched] = useState("");
  const [refusal, setRefusal] = useState<string | null>(null);
  const [asking, setAsking] = useState<{ client: ClientAccountView; gesture: Gesture } | null>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [erasing, setErasing] = useState<ClientAccountView | null>(null);

  const search = async (text: string): Promise<void> => {
    setRefusal(null);
    try {
      const rows = await props.findClients(text.trim());
      setFound(rows);
      setSearched(text.trim());
    } catch (failure) {
      setFound(null);
      setRefusal(messageOf(failure));
    }
  };

  const close = (): void => {
    setAsking(null);
    setReason("");
  };

  const confirm = async (): Promise<void> => {
    if (asking === null || reason.trim() === "") return;
    setBusy(true);
    setRefusal(null);
    try {
      if (asking.gesture === "deactivate") {
        await props.deactivateClient(asking.client.clientId, reason.trim());
      } else {
        await props.reactivateClient(asking.client.clientId, reason.trim());
      }
      setDone(
        fill("console.comptes.clientFait", {
          nom: asking.client.fullName,
          etat:
            asking.gesture === "deactivate"
              ? t("console.comptes.clientEtatDesactive")
              : t("console.comptes.clientEtatActif"),
        }),
      );
      close();
      await search(searched);
    } catch (failure) {
      setRefusal(messageOf(failure));
    } finally {
      setBusy(false);
    }
  };

  const tooShort = query.trim().length < MIN_QUERY;

  return (
    <section data-testid="comptes-clients">
      <h2 className="mb-1 text-sm font-semibold">{t("console.comptes.clientsTitre")}</h2>
      <p data-testid="clients-qui" className="mb-1 text-xs text-gray-500">
        {t("console.comptes.clientsQui")}
      </p>
      <p className="mb-3 text-sm leading-relaxed text-gray-500">{t("console.comptes.clientsSous")}</p>
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
        <form
          data-testid="clients-recherche"
          className="flex items-end gap-3 border-b border-gray-100 px-5 py-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (!tooShort) void search(query);
          }}
        >
          <label className="flex-1 text-xs font-medium text-gray-600">
            {t("console.comptes.clientsRecherche")}
            <input
              data-testid="clients-requete"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
              }}
              className="mt-1.5 block w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-indigo-400"
            />
          </label>
          <button
            type="submit"
            data-testid="clients-rechercher"
            disabled={tooShort}
            title={tooShort ? t("console.comptes.clientsTropCourt") : undefined}
            className="flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-40"
          >
            <Search className="h-4 w-4" aria-hidden="true" />
            {t("console.comptes.clientsRechercher")}
          </button>
        </form>

        {done !== null ? (
          <p
            data-testid="clients-fait"
            className="border-b border-emerald-100 bg-emerald-50 px-5 py-3 text-sm text-emerald-800"
          >
            {done}
          </p>
        ) : null}
        {refusal !== null && asking === null ? (
          <p role="alert" data-testid="clients-refus" className="px-5 py-3 text-sm text-rose-700">
            {refusal}
          </p>
        ) : null}

        {found === null ? null : found.length === 0 ? (
          <p data-testid="clients-aucun" className="px-5 py-4 text-sm text-gray-500">
            {t("console.comptes.clientsAucun")}
          </p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {found.map((client) => (
              <li
                key={client.clientId}
                data-testid={`client-${client.clientId}`}
                className="flex items-center gap-4 px-5 py-3.5"
              >
                <div className="w-64 shrink-0">
                  <p className="text-sm font-medium text-gray-900">{client.fullName}</p>
                  <p className="text-xs text-gray-500">{client.phone}</p>
                </div>
                <div className="flex-1">
                  <span
                    data-testid="client-statut"
                    className={
                      "rounded-full px-2.5 py-1 text-xs font-medium " +
                      (client.anonymised
                        ? "bg-gray-100 text-gray-600"
                        : client.status === "actif"
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-rose-50 text-rose-700")
                    }
                  >
                    {client.anonymised
                      ? t("console.comptes.clientEfface")
                      : client.status === "actif"
                        ? t("console.comptes.clientActif")
                        : t("console.comptes.clientDesactive")}
                  </span>
                  {client.status === "desactive" && client.deactivatedAt !== null ? (
                    <p data-testid="client-motif" className="mt-1.5 text-xs leading-relaxed text-gray-500">
                      {fill("console.comptes.clientDesactiveLe", { date: dateWord(client.deactivatedAt) })}
                      {client.deactivationReason === null
                        ? ""
                        : ` · ${fill("console.comptes.clientMotifLigne", { motif: client.deactivationReason })}`}
                    </p>
                  ) : null}
                </div>
                <span className="flex w-44 shrink-0 flex-col gap-2">
                  {client.status === "actif" ? (
                    <button
                      type="button"
                      data-testid="client-desactiver"
                      onClick={() => {
                        setDone(null);
                        setAsking({ client, gesture: "deactivate" });
                      }}
                      className="rounded-xl border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      {t("console.comptes.clientDesactiver")}
                    </button>
                  ) : client.anonymised ? (
                    <GuardedButton
                      disabled
                      reason={t("console.comptes.clientReactiverEfface")}
                      variant="secondary"
                    >
                      {t("console.comptes.clientReactiver")}
                    </GuardedButton>
                  ) : (
                    <button
                      type="button"
                      data-testid="client-reactiver"
                      onClick={() => {
                        setDone(null);
                        setAsking({ client, gesture: "reactivate" });
                      }}
                      className="rounded-xl border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      {t("console.comptes.clientReactiver")}
                    </button>
                  )}
                  {client.anonymised ? null : props.mayErase ? (
                    <button
                      type="button"
                      data-testid="client-effacer"
                      onClick={() => {
                        setDone(null);
                        setErasing(client);
                      }}
                      className="rounded-xl border border-rose-200 px-3 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50"
                    >
                      {t("console.comptes.clientEffacer")}
                    </button>
                  ) : (
                    <GuardedButton
                      disabled
                      reason={t("console.comptes.clientEffacerSansDroit")}
                      variant="secondary"
                    >
                      {t("console.comptes.clientEffacer")}
                    </GuardedButton>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {erasing === null ? null : (
        <EraseDialog
          client={erasing}
          onClose={() => {
            setErasing(null);
            setRefusal(null);
          }}
          onErase={async (input) => {
            await props.eraseClient(erasing.clientId, input);
            setDone(fill("console.comptes.clientEffacerFait", { nom: erasing.fullName }));
            setErasing(null);
            await search(searched);
          }}
        />
      )}

      {asking === null ? null : (
        <Modal
          open
          title={fill(
            asking.gesture === "deactivate"
              ? "console.comptes.clientDesactiverTitre"
              : "console.comptes.clientReactiverTitre",
            { nom: asking.client.fullName },
          )}
          onClose={close}
          closeLabel={t("console.partenaires.fermer")}
          className="max-w-xl"
        >
          <form
            data-testid="client-modal"
            onSubmit={(event) => {
              event.preventDefault();
              void confirm();
            }}
          >
            <p className="mb-4 text-sm leading-relaxed text-gray-600">
              {asking.gesture === "deactivate"
                ? t("console.comptes.clientDesactiverPhrase")
                : t("console.comptes.clientReactiverPhrase")}
            </p>
            <label className="block text-xs font-medium text-gray-600">
              {t("console.comptes.clientMotif")}
              <textarea
                data-testid="client-motif-saisie"
                value={reason}
                rows={3}
                onChange={(event) => {
                  setReason(event.target.value);
                }}
                className="mt-1.5 block w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-indigo-400"
              />
            </label>
            {refusal === null ? null : (
              <p role="alert" data-testid="client-refus" className="mt-3 text-sm text-rose-700">
                {refusal}
              </p>
            )}
            <div className="mt-5 flex items-center justify-between">
              <button
                type="button"
                onClick={close}
                className="text-sm font-medium text-gray-600 hover:text-gray-700"
              >
                {t("console.comptes.clientAnnuler")}
              </button>
              <button
                type="submit"
                data-testid="client-confirmer"
                disabled={reason.trim() === "" || busy}
                className={
                  "rounded-xl px-5 py-2.5 text-sm font-medium text-white disabled:opacity-40 " +
                  (asking.gesture === "deactivate"
                    ? "bg-rose-600 hover:bg-rose-700"
                    : "bg-indigo-600 hover:bg-indigo-700")
                }
              >
                {asking.gesture === "deactivate"
                  ? t("console.comptes.clientDesactiver")
                  : t("console.comptes.clientReactiver")}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </section>
  );
}

/**
 * Round 9, `M3`: **the erasure dialog, with the friction a force-push gets.**
 *
 * Three things stand between the click and the act, and the door checks the first two itself: a reason (which the
 * audit trail keeps for ever, so the dialog says to name no one), the client's FULL NAME typed back exactly as shown,
 * and a box saying the person understands there is no undo. The red button waits for all three, and a refusal from the
 * door stays in the dialog in the door's own words. Cancel is the quiet default: nothing here is a keystroke away.
 */
export interface PersonErasureWords {
  readonly title: string;
  readonly warning: string;
  readonly goes: string;
  readonly stays: string;
  readonly nameLabel: string;
  /** The test ids' prefix: `client-effacer` for a client, `compte-effacer` for a partner's person. */
  readonly testPrefix: string;
}

/**
 * The erasure dialog, for ANY person: the client's (round 9) and the partner's (partner accounts `M2`) are one component with
 * the same three frictions (a reason, the full name typed back, the irreversibility box) and their own words.
 */
export function PersonErasureDialog(props: {
  readonly fullName: string;
  readonly words: PersonErasureWords;
  readonly onClose: () => void;
  readonly onErase: (input: { reason: string; confirmFullName: string }) => Promise<void>;
}): React.JSX.Element {
  const [reason, setReason] = useState("");
  const [typed, setTyped] = useState("");
  const [understood, setUnderstood] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const ready = reason.trim() !== "" && typed.trim() === props.fullName.trim() && understood;

  return (
    <Modal
      open
      title={props.words.title}
      onClose={props.onClose}
      closeLabel={t("console.partenaires.fermer")}
      className="max-w-xl"
    >
      <form
        data-testid={`${props.words.testPrefix}-modal`}
        onSubmit={(event) => {
          event.preventDefault();
          if (!ready || busy) return;
          void (async () => {
            setBusy(true);
            setRefusal(null);
            try {
              await props.onErase({ reason: reason.trim(), confirmFullName: typed.trim() });
            } catch (failure) {
              setRefusal(messageOf(failure));
              setBusy(false);
            }
          })();
        }}
      >
        <p
          data-testid={`${props.words.testPrefix}-avertissement`}
          className="mb-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium leading-relaxed text-rose-700"
        >
          {props.words.warning}
        </p>
        <p className="mb-2 text-sm leading-relaxed text-gray-600">{props.words.goes}</p>
        <p className="mb-4 text-sm leading-relaxed text-gray-600">{props.words.stays}</p>
        <label className="block text-xs font-medium text-gray-600">
          {t("console.comptes.clientMotif")}
          <textarea
            data-testid={`${props.words.testPrefix}-motif`}
            value={reason}
            rows={2}
            onChange={(event) => {
              setReason(event.target.value);
            }}
            className="mt-1.5 block w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-rose-400"
          />
        </label>
        <p className="mt-1 mb-4 text-sm text-gray-500">{t("console.comptes.clientEffacerMotifNote")}</p>
        <label className="block text-xs font-medium text-gray-600">
          {props.words.nameLabel}
          <input
            data-testid={`${props.words.testPrefix}-nom`}
            value={typed}
            autoComplete="off"
            onChange={(event) => {
              setTyped(event.target.value);
            }}
            className="mt-1.5 block w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-rose-400"
          />
        </label>
        <label className="mt-4 flex items-start gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            data-testid={`${props.words.testPrefix}-comprends`}
            checked={understood}
            onChange={(event) => {
              setUnderstood(event.target.checked);
            }}
            className="mt-0.5"
          />
          {t("console.comptes.clientEffacerComprends")}
        </label>
        {refusal === null ? null : (
          <p
            role="alert"
            data-testid={`${props.words.testPrefix}-refus`}
            className="mt-3 text-sm text-rose-700"
          >
            {refusal}
          </p>
        )}
        <div className="mt-5 flex items-center justify-between">
          <button
            type="button"
            autoFocus
            onClick={props.onClose}
            className="text-sm font-medium text-gray-600 hover:text-gray-700"
          >
            {t("console.comptes.clientAnnuler")}
          </button>
          <button
            type="submit"
            data-testid={`${props.words.testPrefix}-confirmer`}
            disabled={!ready || busy}
            className="rounded-xl bg-rose-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-40"
          >
            {t("console.comptes.clientEffacerBouton")}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function EraseDialog(props: {
  readonly client: ClientAccountView;
  readonly onClose: () => void;
  readonly onErase: (input: { reason: string; confirmFullName: string }) => Promise<void>;
}): React.JSX.Element {
  return (
    <PersonErasureDialog
      fullName={props.client.fullName}
      words={{
        title: fill("console.comptes.clientEffacerTitre", { nom: props.client.fullName }),
        warning: t("console.comptes.clientEffacerAvertissement"),
        goes: t("console.comptes.clientEffacerVa"),
        stays: t("console.comptes.clientEffacerReste"),
        nameLabel: fill("console.comptes.clientEffacerNom", { nom: props.client.fullName }),
        testPrefix: "client-effacer",
      }}
      onClose={props.onClose}
      onErase={props.onErase}
    />
  );
}
